import posthog from 'posthog-js';

/**
 * PostHog product analytics — tracks user behavior, page views, and custom events.
 *
 * SETUP: Set VITE_POSTHOG_KEY in your environment variables.
 * Get yours free at https://app.posthog.com/signup
 *
 * PostHog will not initialize if the key is missing (safe for local dev).
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

export function initPostHog() {
  if (initialized || !POSTHOG_KEY) {
    if (!POSTHOG_KEY) {
      console.warn('[PostHog] No API key found. Set VITE_POSTHOG_KEY in your .env file.');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Capture pageviews automatically on route changes
    capture_pageview: false, // We'll do this manually for SPA route changes
    capture_pageleave: true,
    // Session recording (free tier includes this)
    disable_session_recording: false,
    // Do NOT respect Do Not Track — most browsers send DNT by default,
    // which silently blocks ALL events. Since we don't sell data and only
    // use analytics to improve the student experience, this is appropriate.
    respect_dnt: false,
    // Persistence
    persistence: 'localStorage+cookie',
    // Autocapture clicks, inputs, etc.
    autocapture: true,
    // Person profiles for identified users only (matches PostHog project defaults)
    person_profiles: 'identified_only',
    // Tag events with environment for feature flag segmentation (staging vs production)
    bootstrap: {
      distinctID: undefined,
    },
    loaded: (ph) => {
      ph.register({ environment: import.meta.env.MODE });
    },
  });

  initialized = true;
}

/**
 * Identify a logged-in user so PostHog links all their events to one profile.
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.identify(userId, properties);
}

/**
 * Reset identity on logout so the next user gets a fresh anonymous ID.
 */
export function resetUser() {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Track a page view (call on every SPA route change).
 */
export function trackPageView(path?: string) {
  if (!initialized) return;
  posthog.capture('$pageview', {
    $current_url: path || window.location.href,
  });
}

/**
 * Track a custom event (e.g. 'scholarship_saved', 'onboarding_completed').
 */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.capture(event, properties);
}

export { posthog };
